import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
    Pagination,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    useTheme,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import CircularProgress from '@mui/material/CircularProgress';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UndoIcon from '@mui/icons-material/Undo';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { useSnackbar } from 'notistack';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e4976' : '#1976d2',
        color: theme.palette.common.white,
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const AutoReports = () => {
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [searchParams, setSearchParams] = useSearchParams();

    // ?include_dev=1 URL param seeds the toggle (the dev test page deep-links here)
    const [includeDev, setIncludeDev] = useState(
        searchParams.get('include_dev') === '1' || searchParams.get('include_dev') === 'true'
    );

    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

    // Filters
    const [triggerTypeFilter, setTriggerTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Detail dialog (Logs table)
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailLoadingId, setDetailLoadingId] = useState(null);

    // Pending Review panel
    const [pendingItems, setPendingItems] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);

    // Review Dialog (confirm/skip pending-review items)
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [reviewingLog, setReviewingLog] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    // Inline-edit mode inside the Review Dialog (3-remaining only for now)
    const [editMode, setEditMode] = useState(false);
    const [draftSubject, setDraftSubject] = useState('');
    const [draftContent, setDraftContent] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    // Scan trigger
    const [scanLoading, setScanLoading] = useState(false);
    const [scanTarget, setScanTarget] = useState('all'); // 'all' | '2rem' | '3rem' | 'midterm'
    const [scanConfirmOpen, setScanConfirmOpen] = useState(false);
    const [scanPassword, setScanPassword] = useState('');
    const SCAN_PASSWORD = 'aone0819';
    const pollIntervalRef = useRef(null);

    // Daily digest
    const [digestOpen, setDigestOpen] = useState(true);
    const [digestDate, setDigestDate] = useState(new Date().toISOString().split('T')[0]);
    const [digestData, setDigestData] = useState(null);
    const [digestLoading, setDigestLoading] = useState(false);

    useEffect(() => {
        fetchLogs();
    }, [triggerTypeFilter, statusFilter, pagination.page]);

    useEffect(() => {
        fetchDigest();
    }, [digestDate]);

    useEffect(() => {
        fetchPending();
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeDev]);

    // Keep the URL in sync so deep links remain stable
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (includeDev) next.set('include_dev', '1');
        else next.delete('include_dev');
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeDev]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (triggerTypeFilter !== 'all') params.append('trigger_type', triggerTypeFilter);
            if (statusFilter !== 'all') params.append('status', statusFilter);
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);

            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports?${params.toString()}`,
                { headers: { Authorization: getAuthHeader() } }
            );

            setLogs(response.data.logs);
            setPagination(prev => ({
                ...prev,
                total: response.data.pagination.total,
                totalPages: response.data.pagination.totalPages,
            }));
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar('Failed to fetch auto report logs', { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchDigest = async () => {
        setDigestLoading(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/digest?date=${digestDate}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setDigestData(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar('Failed to fetch digest', { variant: 'error' });
            }
        } finally {
            setDigestLoading(false);
        }
    };

    const fetchLogDetail = async (id) => {
        setDetailLoadingId(id);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${id}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setSelectedLog(response.data);
            setDetailDialogOpen(true);
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar('Failed to fetch log details', { variant: 'error' });
            }
        } finally {
            setDetailLoadingId(null);
        }
    };

    const fetchPending = async () => {
        setPendingLoading(true);
        try {
            const qs = includeDev ? '?include_dev=1' : '';
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/pending-review${qs}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setPendingItems(response.data.items || []);
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar('Failed to fetch pending items', { variant: 'error' });
            }
        } finally {
            setPendingLoading(false);
        }
    };

    const openReviewDialog = async (logId) => {
        setReviewLoading(true);
        setReviewingLog(null);
        setReviewDialogOpen(true);
        setEditMode(false);
        setDraftSubject('');
        setDraftContent('');
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/pending-review/${logId}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setReviewingLog(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to load review content', { variant: 'error' });
                setReviewDialogOpen(false);
            }
        } finally {
            setReviewLoading(false);
        }
    };

    const refreshAfterAction = () => {
        fetchPending();
        fetchDigest();
        fetchLogs();
    };

    const confirmLog = async (logId) => {
        setActionLoadingId(logId);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/confirm`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('Confirmed — queued for the 8 PM ET send', { variant: 'success' });
            if (reviewingLog?.id === logId) {
                setReviewDialogOpen(false);
                setReviewingLog(null);
            }
            refreshAfterAction();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to confirm', { variant: 'error' });
            }
        } finally {
            setActionLoadingId(null);
        }
    };

    const unconfirmLog = async (logId) => {
        setActionLoadingId(logId);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/unconfirm`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('Un-confirmed — moved back to Pending Review', { variant: 'info' });
            if (reviewingLog?.id === logId) {
                setReviewDialogOpen(false);
                setReviewingLog(null);
            }
            refreshAfterAction();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to un-confirm', { variant: 'error' });
            }
        } finally {
            setActionLoadingId(null);
        }
    };

    const reopenLog = async (logId) => {
        setActionLoadingId(logId);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/reopen`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('Re-opened — moved back to Pending Review', { variant: 'info' });
            refreshAfterAction();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to re-open', { variant: 'error' });
            }
        } finally {
            setActionLoadingId(null);
        }
    };

    const markSent = async (logId) => {
        setActionLoadingId(logId);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/mark-sent`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('Marked as Email Sent', { variant: 'success' });
            refreshAfterAction();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to mark as sent', { variant: 'error' });
            }
        } finally {
            setActionLoadingId(null);
        }
    };

    const enterEditMode = () => {
        if (!reviewingLog) return;
        setDraftSubject(reviewingLog.email_subject || '');
        setDraftContent(reviewingLog.email_content || '');
        setEditMode(true);
    };

    const cancelEdit = () => {
        setEditMode(false);
        setDraftSubject('');
        setDraftContent('');
    };

    const saveEdit = async () => {
        if (!reviewingLog) return;
        const subject = draftSubject.trim();
        if (!subject) {
            enqueueSnackbar('Subject cannot be empty', { variant: 'warning' });
            return;
        }
        setSavingEdit(true);
        try {
            const response = await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${reviewingLog.id}`,
                { email_subject: subject, email_content: draftContent },
                { headers: { Authorization: getAuthHeader() } }
            );
            setReviewingLog(prev => prev ? {
                ...prev,
                email_subject: response.data.email_subject,
                email_content: response.data.email_content,
            } : prev);
            setEditMode(false);
            enqueueSnackbar('Email updated', { variant: 'success' });
            // Refresh the Logs table so the parent list reflects any subject change
            fetchLogs();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to save edits', { variant: 'error' });
            }
        } finally {
            setSavingEdit(false);
        }
    };

    const skipLog = async (logId) => {
        setActionLoadingId(logId);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/${logId}/skip`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            enqueueSnackbar('Skipped — will not be sent', { variant: 'info' });
            if (reviewingLog?.id === logId) {
                setReviewDialogOpen(false);
                setReviewingLog(null);
            }
            refreshAfterAction();
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar(error.response?.data?.error || 'Failed to skip', { variant: 'error' });
            }
        } finally {
            setActionLoadingId(null);
        }
    };

    const runScan = async () => {
        setScanLoading(true);
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/auto-reports/trigger-scan`,
                { target: scanTarget },
                { headers: { Authorization: getAuthHeader() } }
            );
            const targetLabel = scanTarget === 'all' ? 'All triggers'
                : scanTarget === 'midterm' ? 'Midterm'
                : scanTarget === '3rem' ? '3-Remaining'
                : '2-Remaining';
            enqueueSnackbar(`Scan started (${targetLabel}) — content is generating. Pending list will refresh.`, { variant: 'info' });
            // Poll for ~30s to pick up async content generation. Stash the interval
            // on a ref so unmount and repeat clicks don't leak timers.
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            let tries = 6;
            pollIntervalRef.current = setInterval(() => {
                fetchPending();
                if (--tries <= 0) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                }
            }, 5000);
        } catch (error) {
            if (error.response?.status === 401) {
                signOut();
                navigate('/signin');
            } else {
                enqueueSnackbar('Failed to send reports', { variant: 'error' });
            }
        } finally {
            setScanLoading(false);
        }
    };

    const getTriggerTypeChip = (type) => {
        const config = {
            midterm_report: { label: 'Midterm Report', color: 'primary' },
            three_remaining_email: { label: '3 Remaining', color: 'warning' },
            two_remaining_email: { label: '2 Remaining', color: 'info' },
        };
        const c = config[type] || { label: type, color: 'default' };
        return <Chip label={c.label} color={c.color} size="small" />;
    };

    const getStatusChip = (status) => {
        const config = {
            pending: { label: 'Pending', color: 'default', icon: <HourglassEmptyIcon /> },
            processing: { label: 'Processing', color: 'warning', icon: <SyncIcon /> },
            completed: { label: 'Completed', color: 'success', icon: <CheckCircleIcon /> },
            email_sent: { label: 'Email Sent', color: 'success', icon: <MarkEmailReadIcon /> },
            email_failed: { label: 'Email Failed', color: 'error', icon: <MarkEmailUnreadIcon /> },
            failed: { label: 'Failed', color: 'error', icon: <ErrorIcon /> },
            skipped: { label: 'Skipped', color: 'default', icon: <SkipNextIcon /> },
            office_notified: { label: 'Office Notified', color: 'info', icon: <NotificationsActiveIcon /> },
            pending_review: { label: 'Pending Review', color: 'info', icon: <VisibilityIcon /> },
            confirmed: { label: 'Confirmed', color: 'success', icon: <CheckCircleIcon /> },
            expired_unconfirmed: { label: 'Expired', color: 'default', icon: <HourglassEmptyIcon /> },
            permanently_failed: { label: 'Permanently Failed', color: 'error', icon: <ErrorIcon /> },
            preview: { label: 'Preview', color: 'default', icon: <VisibilityIcon /> },
            preview_ready: { label: 'Ready for Review', color: 'info', icon: <VisibilityIcon /> },
        };
        const c = config[status] || { label: status, color: 'default' };
        return <Chip label={c.label} color={c.color} size="small" icon={c.icon} />;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    return (
        <Box>
            {/* Pending Review Panel */}
            <Paper sx={{ mb: 2, p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                    {(() => {
                        const pendingCount = pendingItems.filter(i => i.status === 'pending_review').length;
                        const confirmedCount = pendingItems.filter(i => i.status === 'confirmed').length;
                        const skippedCount = pendingItems.filter(i => i.status === 'skipped').length;
                        return (
                            <Typography variant="h6" fontWeight="bold">
                                Pending Review — <span style={{ color: '#0288d1' }}>{pendingCount} pending</span>
                                {confirmedCount > 0 && (
                                    <>, <span style={{ color: '#2e7d32' }}>{confirmedCount} queued for 8 PM</span></>
                                )}
                                {skippedCount > 0 && (
                                    <>, <span style={{ color: '#999' }}>{skippedCount} skipped</span></>
                                )}
                            </Typography>
                        );
                    })()}
                    <Box display="flex" gap={1} alignItems="center">
                        <Tooltip title="Include dev_test rows (from the Dev Testing page)">
                            <Button
                                size="small"
                                variant={includeDev ? 'contained' : 'outlined'}
                                color={includeDev ? 'warning' : 'inherit'}
                                onClick={() => setIncludeDev(v => !v)}
                            >
                                {includeDev ? 'Dev rows: ON' : 'Dev rows: OFF'}
                            </Button>
                        </Tooltip>
                        <TextField
                            select
                            label="Target"
                            value={scanTarget}
                            onChange={(e) => setScanTarget(e.target.value)}
                            size="small"
                            disabled={scanLoading}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="all">All triggers</MenuItem>
                            <MenuItem value="midterm">Midterm</MenuItem>
                            <MenuItem value="3rem">3-Remaining</MenuItem>
                            <MenuItem value="2rem">2-Remaining</MenuItem>
                        </TextField>
                        <Button
                            size="small"
                            onClick={fetchPending}
                            disabled={pendingLoading}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={scanLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                            onClick={() => setScanConfirmOpen(true)}
                            disabled={scanLoading}
                            size="small"
                        >
                            {scanLoading ? 'Scanning...' : 'Run Scan'}
                        </Button>
                    </Box>
                </Box>

                {pendingLoading && pendingItems.length === 0 ? (
                    <Loading />
                ) : pendingItems.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" py={2}>
                        No items awaiting review. Run a scan to detect new midterm or 3-remaining triggers.
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Course</StyledTableCell>
                                    <StyledTableCell>Trigger</StyledTableCell>
                                    <StyledTableCell>Recipient</StyledTableCell>
                                    <StyledTableCell>Generated</StyledTableCell>
                                    <StyledTableCell>Expires In</StyledTableCell>
                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pendingItems.map(item => {
                                    const expiring = item.days_remaining != null && item.days_remaining <= 2;
                                    const busy = actionLoadingId === item.id;
                                    const isPending = item.status === 'pending_review';
                                    const isConfirmed = item.status === 'confirmed';
                                    const isSkipped = item.status === 'skipped';
                                    return (
                                        <TableRow key={item.id} hover sx={item.is_dev_test ? { backgroundColor: alpha(theme.palette.warning.main, 0.06) } : undefined}>
                                            <TableCell>
                                                <Box display="flex" gap={0.5} alignItems="center">
                                                    {getStatusChip(item.status)}
                                                    {item.is_dev_test && (
                                                        <Chip label="DEV" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{item.student_name}</TableCell>
                                            <TableCell>{item.course_name}</TableCell>
                                            <TableCell>{getTriggerTypeChip(item.trigger_type)}</TableCell>
                                            <TableCell>{item.recipient_email || '-'}</TableCell>
                                            <TableCell>{formatDate(item.triggered_at)}</TableCell>
                                            <TableCell>
                                                {item.days_remaining != null ? (
                                                    <Chip
                                                        label={`${item.days_remaining}d`}
                                                        size="small"
                                                        color={expiring ? 'warning' : 'default'}
                                                        variant={expiring ? 'filled' : 'outlined'}
                                                    />
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {!isSkipped && (
                                                    <Tooltip title="Review content">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openReviewDialog(item.id)}
                                                                disabled={busy}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {isPending && (
                                                    <>
                                                        <Tooltip title="Confirm (queue for 8 PM send)">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={() => confirmLog(item.id)}
                                                                    disabled={busy || !item.has_content}
                                                                >
                                                                    {busy ? <CircularProgress size={18} /> : <CheckCircleIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Skip (never send)">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => skipLog(item.id)}
                                                                    disabled={busy}
                                                                >
                                                                    <SkipNextIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {isConfirmed && (
                                                    <Tooltip title="Un-confirm (back to Pending Review)">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="warning"
                                                                onClick={() => unconfirmLog(item.id)}
                                                                disabled={busy}
                                                            >
                                                                <UndoIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {isSkipped && (
                                                    <Tooltip title="Re-open (back to Pending Review)">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                onClick={() => reopenLog(item.id)}
                                                                disabled={busy}
                                                            >
                                                                <ReplayIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Daily Digest Panel */}
            <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.5, cursor: 'pointer', backgroundColor: colors.primary[600] }}
                    onClick={() => setDigestOpen(!digestOpen)}
                >
                    <Typography variant="h6" fontWeight="bold">Daily Digest</Typography>
                    {digestOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={digestOpen}>
                    <Box sx={{ p: 2 }}>
                        <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                type="date"
                                label="Date"
                                value={digestDate}
                                onChange={(e) => setDigestDate(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 180 }}
                            />
                        </Box>

                        {digestLoading ? (
                            <Loading />
                        ) : digestData ? (
                            <>
                                {/* Counter Bar */}
                                <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
                                    {[
                                        { key: 'sent', label: 'Sent', color: 'success.main', value: digestData.counts?.sent ?? 0 },
                                        { key: 'skipped', label: 'Skipped', color: 'text.primary', value: digestData.counts?.skipped ?? 0 },
                                        { key: 'failed', label: 'Failed (will retry)', color: 'warning.main', value: digestData.counts?.failed ?? 0 },
                                        { key: 'pending', label: 'Pending (all dates)', color: 'info.main', value: digestData.counts?.pending ?? 0 },
                                        { key: 'office_notified', label: 'Awaiting admin compose (2-rem)', color: 'warning.main', value: digestData.counts?.office_notified ?? 0 },
                                        { key: 'expired', label: 'Expired', color: 'text.secondary', value: digestData.counts?.expired ?? 0 },
                                        { key: 'permanently_failed', label: 'Permanently Failed', color: 'error.main', value: digestData.counts?.permanently_failed ?? 0 },
                                    ].map(c => (
                                        <Paper
                                            key={c.key}
                                            variant="outlined"
                                            sx={{ p: 1.5, minWidth: 140, flex: '1 1 140px' }}
                                        >
                                            <Typography variant="caption" color="textSecondary" display="block">{c.label}</Typography>
                                            <Typography variant="h5" fontWeight="bold" sx={{ color: c.color, mt: 0.5 }}>{c.value}</Typography>
                                        </Paper>
                                    ))}
                                </Box>

                                {/* Full Details Table */}
                                {digestData.logs.length > 0 && (
                                    <Box mt={2}>
                                        <Typography variant="subtitle2" mb={1}>
                                            Details
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell>Student</StyledTableCell>
                                                        <StyledTableCell>Course</StyledTableCell>
                                                        <StyledTableCell>Trigger Type</StyledTableCell>
                                                        <StyledTableCell>Status</StyledTableCell>
                                                        <StyledTableCell>Recipient</StyledTableCell>
                                                        <StyledTableCell>Error</StyledTableCell>
                                                        <StyledTableCell>Auto-Retry?</StyledTableCell>
                                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {digestData.logs.map(log => {
                                                        const isFailed = log.status === 'failed' || log.status === 'email_failed';
                                                        const isInProgress = log.status === 'processing' || log.status === 'pending';
                                                        return (
                                                            <TableRow
                                                                key={log.id}
                                                                sx={{
                                                                    backgroundColor: isFailed
                                                                        ? alpha(theme.palette.error.main, 0.08)
                                                                        : isInProgress
                                                                            ? alpha(theme.palette.warning.main, 0.08)
                                                                            : 'inherit',
                                                                }}
                                                            >
                                                                <TableCell>{log.student_name}</TableCell>
                                                                <TableCell>{log.course_name}</TableCell>
                                                                <TableCell>{getTriggerTypeChip(log.trigger_type)}</TableCell>
                                                                <TableCell>{getStatusChip(log.status)}</TableCell>
                                                                <TableCell>{log.recipient_email || '-'}</TableCell>
                                                                <TableCell>
                                                                    {log.error_message ? (
                                                                        <Typography variant="body2" color="error.main" sx={{ fontSize: 12 }}>
                                                                            {log.error_message}
                                                                        </Typography>
                                                                    ) : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {isFailed ? (
                                                                        <Chip
                                                                            label={log.will_auto_retry || 'Unknown'}
                                                                            size="small"
                                                                            color={log.will_auto_retry?.startsWith('Yes') ? 'success' : 'error'}
                                                                            variant="outlined"
                                                                        />
                                                                    ) : null}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {log.trigger_type === 'two_remaining_email' && log.status === 'office_notified' && (
                                                                        <>
                                                                            <Tooltip title="Mark as Email Sent (after office sends the parent email)">
                                                                                <span>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="success"
                                                                                        onClick={() => markSent(log.id)}
                                                                                        disabled={actionLoadingId === log.id}
                                                                                    >
                                                                                        {actionLoadingId === log.id ? <CircularProgress size={18} /> : <MarkEmailReadIcon fontSize="small" />}
                                                                                    </IconButton>
                                                                                </span>
                                                                            </Tooltip>
                                                                            <Tooltip title="Skip (never send)">
                                                                                <span>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => skipLog(log.id)}
                                                                                        disabled={actionLoadingId === log.id}
                                                                                    >
                                                                                        <SkipNextIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                </span>
                                                                            </Tooltip>
                                                                        </>
                                                                    )}
                                                                    {log.trigger_type === 'two_remaining_email' && log.status === 'skipped' && (
                                                                        <Tooltip title="Re-open (back to Office Notified)">
                                                                            <span>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="info"
                                                                                    onClick={() => reopenLog(log.id)}
                                                                                    disabled={actionLoadingId === log.id}
                                                                                >
                                                                                    <ReplayIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </span>
                                                                        </Tooltip>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                    </Box>
                                )}
                            </>
                        ) : null}
                    </Box>
                </Collapse>
            </Paper>

            {/* Filters */}
            <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
                <TextField
                    select
                    label="Trigger Type"
                    value={triggerTypeFilter}
                    onChange={(e) => {
                        setTriggerTypeFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    size="small"
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="midterm_report">Midterm Report</MenuItem>
                    <MenuItem value="three_remaining_email">3 Remaining</MenuItem>
                    <MenuItem value="two_remaining_email">2 Remaining</MenuItem>
                </TextField>

                <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    size="small"
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="email_sent">Email Sent</MenuItem>
                    <MenuItem value="email_failed">Email Failed</MenuItem>
                    <MenuItem value="skipped">Skipped</MenuItem>
                    <MenuItem value="expired_unconfirmed">Expired</MenuItem>
                    <MenuItem value="permanently_failed">Permanently Failed</MenuItem>
                    <MenuItem value="office_notified">Office Notified</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="pending">Pending (legacy)</MenuItem>
                </TextField>

                <Box flexGrow={1} />
            </Box>

            {/* Table */}
            {loading ? (
                <Loading />
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Date</StyledTableCell>
                                    <StyledTableCell>Student</StyledTableCell>
                                    <StyledTableCell>Course</StyledTableCell>
                                    <StyledTableCell>Trigger Type</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>Recipient</StyledTableCell>
                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="textSecondary" py={3}>
                                                No auto report logs found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell>{formatDate(log.triggered_at)}</TableCell>
                                            <TableCell>{log.student_name}</TableCell>
                                            <TableCell>{log.course_name}</TableCell>
                                            <TableCell>{getTriggerTypeChip(log.trigger_type)}</TableCell>
                                            <TableCell>{getStatusChip(log.status)}</TableCell>
                                            <TableCell>{log.recipient_email || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => fetchLogDetail(log.id)}
                                                        disabled={detailLoadingId != null}
                                                    >
                                                        {detailLoadingId === log.id ? <CircularProgress size={18} /> : <VisibilityIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                {log.trigger_type === 'two_remaining_email' && log.status === 'office_notified' && (
                                                    <>
                                                        <Tooltip title="Mark as Email Sent (after office sends the parent email)">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={() => markSent(log.id)}
                                                                    disabled={actionLoadingId === log.id}
                                                                >
                                                                    {actionLoadingId === log.id ? <CircularProgress size={18} /> : <MarkEmailReadIcon fontSize="small" />}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Skip (never send)">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => skipLog(log.id)}
                                                                    disabled={actionLoadingId === log.id}
                                                                >
                                                                    <SkipNextIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {log.trigger_type === 'two_remaining_email' && log.status === 'skipped' && (
                                                    <Tooltip title="Re-open (back to Office Notified)">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                onClick={() => reopenLog(log.id)}
                                                                disabled={actionLoadingId === log.id}
                                                            >
                                                                <ReplayIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pagination.totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                                count={pagination.totalPages}
                                page={pagination.page}
                                onChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Run Scan Confirmation Dialog */}
            <Dialog
                open={scanConfirmOpen}
                onClose={() => { setScanConfirmOpen(false); setScanPassword(''); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Run Scan — {scanTarget === 'all' ? 'All Triggers' : scanTarget === 'midterm' ? 'Midterm' : scanTarget === '3rem' ? '3-Remaining' : '2-Remaining'}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" mb={2}>
                        The scan will check every active enrollment and create
                        pending-review items for any student who just hit the selected milestone.
                        Nothing is sent to parents automatically — every midterm and 3-remaining item
                        waits in Pending Review until you click Confirm.
                    </Typography>
                    <Typography variant="subtitle2" mb={0.5}>What this will do:</Typography>
                    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
                        {(scanTarget === 'all' || scanTarget === 'midterm') && (
                            <li>
                                <strong>Midterm:</strong> AI generates a full PDF report for each student at midpoint.
                                Each report takes <strong>5–15 minutes</strong> and lands in Pending Review when ready.
                            </li>
                        )}
                        {(scanTarget === 'all' || scanTarget === '3rem') && (
                            <li>
                                <strong>3-Remaining:</strong> AI writes a progress summary email for each student with 3 sessions left.
                                Lands in Pending Review within ~1 minute.
                            </li>
                        )}
                        {(scanTarget === 'all' || scanTarget === '2rem') && (
                            <li>
                                <strong>2-Remaining:</strong> A batch office notification email fires <strong>immediately</strong> with
                                Compose Email links (no preview gate — office composes manually).
                            </li>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Duplicates are automatically skipped — if a log already exists for a student's milestone, the scan won't re-create it.
                        New rows will appear in the Pending Review list as generation finishes.
                    </Typography>
                    <TextField
                        type="password"
                        label="Admin password"
                        placeholder="Enter password to enable Run Scan"
                        value={scanPassword}
                        onChange={(e) => setScanPassword(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        disabled={scanLoading}
                        error={scanPassword.length > 0 && scanPassword !== SCAN_PASSWORD}
                        helperText={scanPassword.length > 0 && scanPassword !== SCAN_PASSWORD ? 'Incorrect password' : ' '}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setScanConfirmOpen(false); setScanPassword(''); }} disabled={scanLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={scanLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                        onClick={async () => {
                            setScanConfirmOpen(false);
                            setScanPassword('');
                            await runScan();
                        }}
                        disabled={scanLoading || scanPassword !== SCAN_PASSWORD}
                    >
                        {scanLoading ? 'Scanning...' : 'Run Scan'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Auto Report Log Details
                </DialogTitle>
                <DialogContent dividers>
                    {selectedLog && (
                        <Box>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Student</Typography>
                                    <Typography>{selectedLog.student_name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Course</Typography>
                                    <Typography>{selectedLog.course_name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Trigger Type</Typography>
                                    <Box mt={0.5}>{getTriggerTypeChip(selectedLog.trigger_type)}</Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Status</Typography>
                                    <Box mt={0.5}>{getStatusChip(selectedLog.status)}</Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Recipient</Typography>
                                    <Typography>{selectedLog.recipient_email || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Triggered At</Typography>
                                    <Typography>{formatDate(selectedLog.triggered_at)}</Typography>
                                </Box>
                                {selectedLog.completed_at && (
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Completed At</Typography>
                                        <Typography>{formatDate(selectedLog.completed_at)}</Typography>
                                    </Box>
                                )}
                                {selectedLog.report_id && (
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Report ID</Typography>
                                        <Typography>{selectedLog.report_id}</Typography>
                                    </Box>
                                )}
                            </Box>

                            {selectedLog.error_message && (
                                <Box mb={2} p={2} bgcolor="error.light" borderRadius={1}>
                                    <Typography variant="caption" color="error.contrastText">Error</Typography>
                                    <Typography variant="body2" color="error.contrastText">{selectedLog.error_message}</Typography>
                                </Box>
                            )}

                            {selectedLog.email_subject && (
                                <Box mb={2}>
                                    <Typography variant="caption" color="textSecondary">Email Subject</Typography>
                                    <Typography variant="body1" fontWeight="bold">{selectedLog.email_subject}</Typography>
                                </Box>
                            )}

                            {selectedLog.email_content && (
                                <Box mb={2}>
                                    <Typography variant="caption" color="textSecondary" display="block" mb={1}>Email Content</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{selectedLog.email_content}</div>
                                    </Paper>
                                </Box>
                            )}

                            {selectedLog.metadata && (
                                <Box>
                                    <Typography variant="caption" color="textSecondary" display="block" mb={1}>Metadata</Typography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(selectedLog.metadata, null, 2)}
                                        </pre>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Review Dialog */}
            <Dialog
                open={reviewDialogOpen}
                onClose={() => {
                    if (!actionLoadingId && !savingEdit) {
                        setReviewDialogOpen(false);
                        setReviewingLog(null);
                        cancelEdit();
                    }
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Review Auto-Report</DialogTitle>
                <DialogContent dividers>
                    {reviewLoading || !reviewingLog ? (
                        <Loading />
                    ) : (
                        <Box>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Student</Typography>
                                    <Typography>{reviewingLog.student_name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Course</Typography>
                                    <Typography>{reviewingLog.course_name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Trigger</Typography>
                                    <Box mt={0.5}>{getTriggerTypeChip(reviewingLog.trigger_type)}</Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Recipient</Typography>
                                    <Typography>{reviewingLog.recipient_email || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Generated</Typography>
                                    <Typography>{formatDate(reviewingLog.triggered_at)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">Review Deadline</Typography>
                                    <Typography>{formatDate(reviewingLog.review_deadline)}</Typography>
                                </Box>
                                {reviewingLog.status === 'confirmed' && reviewingLog.confirmed_at && (
                                    <Box>
                                        <Typography variant="caption" color="textSecondary">Confirmed At</Typography>
                                        <Typography>{formatDate(reviewingLog.confirmed_at)}</Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Email subject + body — shown for both midterm and 3-remaining.
                                Edit mode (3-remaining only) swaps the display for editable inputs. */}
                            {editMode ? (
                                <>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Email Subject</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={draftSubject}
                                            onChange={(e) => setDraftSubject(e.target.value)}
                                            disabled={savingEdit}
                                            inputProps={{ maxLength: 500 }}
                                        />
                                    </Box>
                                    <Box mb={2}>
                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Email Body</Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={8}
                                            maxRows={16}
                                            value={draftContent}
                                            onChange={(e) => setDraftContent(e.target.value)}
                                            disabled={savingEdit}
                                            inputProps={{ maxLength: 50000 }}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <>
                                    {reviewingLog.email_subject && (
                                        <Box mb={2}>
                                            <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Email Subject</Typography>
                                            <Typography variant="body1" fontWeight="bold">{reviewingLog.email_subject}</Typography>
                                        </Box>
                                    )}
                                    {reviewingLog.email_content && (
                                        <Box mb={2}>
                                            <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>Email Body</Typography>
                                            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                                                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                                                    {reviewingLog.email_content}
                                                </div>
                                            </Paper>
                                        </Box>
                                    )}
                                </>
                            )}

                            {/* PDF report preview — midterm only */}
                            {reviewingLog.trigger_type === 'midterm_report' && (
                                reviewingLog.report_html ? (
                                    <Box sx={{ border: 1, borderColor: 'divider', height: 520, borderRadius: 1, overflow: 'hidden' }}>
                                        <iframe
                                            title="Midterm Report Preview"
                                            srcDoc={reviewingLog.report_html}
                                            style={{ width: '100%', height: '100%', border: 'none' }}
                                            sandbox="allow-same-origin allow-scripts"
                                        />
                                    </Box>
                                ) : (
                                    <Typography color="textSecondary">
                                        Report content is still generating. Close this dialog and try again in a minute.
                                    </Typography>
                                )
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {editMode ? (
                        <>
                            <Button
                                onClick={cancelEdit}
                                disabled={savingEdit}
                                startIcon={<CloseIcon />}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={saveEdit}
                                disabled={savingEdit}
                                startIcon={savingEdit ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            >
                                Save
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={() => { setReviewDialogOpen(false); setReviewingLog(null); cancelEdit(); }}
                                disabled={!!actionLoadingId}
                            >
                                Close
                            </Button>
                            {reviewingLog?.status === 'pending_review' && reviewingLog?.trigger_type === 'three_remaining_email' && (
                                <Button
                                    color="info"
                                    startIcon={<EditIcon />}
                                    onClick={enterEditMode}
                                    disabled={!reviewingLog || !!actionLoadingId}
                                >
                                    Edit
                                </Button>
                            )}
                            {reviewingLog?.status === 'pending_review' && (
                                <>
                                    <Button
                                        color="warning"
                                        startIcon={<SkipNextIcon />}
                                        onClick={() => reviewingLog && skipLog(reviewingLog.id)}
                                        disabled={!reviewingLog || !!actionLoadingId}
                                    >
                                        Skip
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={actionLoadingId === reviewingLog?.id ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                                        onClick={() => reviewingLog && confirmLog(reviewingLog.id)}
                                        disabled={!reviewingLog || !!actionLoadingId}
                                    >
                                        Confirm (queue for 8 PM)
                                    </Button>
                                </>
                            )}
                            {reviewingLog?.status === 'confirmed' && (
                                <Button
                                    color="warning"
                                    startIcon={<UndoIcon />}
                                    onClick={() => reviewingLog && unconfirmLog(reviewingLog.id)}
                                    disabled={!reviewingLog || !!actionLoadingId}
                                >
                                    Un-confirm
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AutoReports;
